export const debounce = (callback: (args: unknown) => void, delay: number): ((args: unknown) => void) => {
  let timer: NodeJS.Timeout;

  return (args: unknown): void => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      callback(args);
      clearTimeout(timer);
    }, delay);
  };
};

/**
 * Fallback for browsers where the async Clipboard API is unavailable — most
 * notably iOS (Safari *and* Chrome, which is WebKit too) over plain http:
 * navigator.clipboard only exists in a secure context, i.e. https or localhost.
 *
 * Copies a hidden span rather than an input, because iOS ignores
 * input.select(), and writes the value straight onto the copy event so the
 * result does not depend on how the browser resolved the selection.
 */
const copyWithExecCommand = (value: string): boolean => {
  const mark = document.createElement('span');
  mark.textContent = value;
  mark.setAttribute('aria-hidden', 'true');
  // Reset inherited styles first — a global `user-select: none` would otherwise
  // make the range uncopyable.
  mark.style.all = 'unset';
  mark.style.position = 'fixed';
  mark.style.top = '0';
  mark.style.clip = 'rect(0, 0, 0, 0)';
  mark.style.whiteSpace = 'pre';
  mark.style.userSelect = 'text';
  mark.style.setProperty('-webkit-user-select', 'text');

  const handleCopy = (event: ClipboardEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    event.clipboardData?.clearData();
    event.clipboardData?.setData('text/plain', value);
  };

  mark.addEventListener('copy', handleCopy);
  document.body.appendChild(mark);

  const selection = document.getSelection();
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  const range = document.createRange();
  range.selectNodeContents(mark);
  selection?.removeAllRanges();
  selection?.addRange(range);

  let isCopied = false;
  try {
    isCopied = document.execCommand('copy');
  } catch (_error) {
    isCopied = false;
  }

  mark.removeEventListener('copy', handleCopy);
  document.body.removeChild(mark);

  selection?.removeAllRanges();
  if (previousRange) {
    selection?.addRange(previousRange);
  }

  return isCopied;
};

export const copyValue = async (value: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_error) {
      // Permission denied, or a WebView that exposes the API but refuses the
      // write — fall back below.
    }
  }

  return copyWithExecCommand(value);
};

export const getLocationPathLastItem = (pathname: string): string => {
  const pathNameArray = pathname.split('/');
  return pathNameArray[pathNameArray.length - 1] as string;
};
